import handleError from '@fuse/core/errorHandler';
import { Button, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useMessage from 'src/app/hooks/useMessage';
import {
  useCreateProjectStatusMutation,
  useEditProjectStatusMutation,
} from 'src/app/services/ticket/projectsApi';

const defaultValues = {
  nameUz: '',
  nameRu: '',
};

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const ProjectStatusForm = ({ projectStatus, isEdit, onClose, refetch }) => {
  const { t } = useTranslation('translations');
  const { showMessage } = useMessage();
  const [update, { isLoading }] = useEditProjectStatusMutation();
  const [create, { isLoading: isCreateLoading, error: errorCreate }] =
    useCreateProjectStatusMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: projectStatus ?? defaultValues,
  });

  const handleClose = () => {
    onClose?.();
    reset(defaultValues);
    refetch?.();
  };

  const trimObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    );

  const makeRules = () => ({
    required: t('RequiredField') || true,
    validate: (value) => {
      const v = (value ?? '').trim();
      if (!v) return t('EmptyInput') || 'This field cannot be empty';
      if (!ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    fieldOnChange(e.target.value.trimStart());
  };

  const onSubmit = async (values) => {
    if (!isValid) return;

    const payload = trimObject(values);
    const message = isEdit
      ? t('ProjectStatusSuccessfullyUpdated')
      : t('ProjectStatusSuccessfullyAdded');

    const promise = isEdit ? update({ id: projectStatus?.id, ...payload }) : create(payload);

    promise
      .unwrap()
      .then(() => {
        showMessage({ message, variant: 'success' });
        handleClose();
      })
      .catch((err) => handleError({ response: err }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-10 pt-10 max-w-7xl">
      <Controller
        name="nameUz"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <>
            {errorCreate?.data?.errors?.nameUz && (
              <p className="-mb-4 text-red-500">{t('RequiredField')}</p>
            )}
            <TextField
              {...field}
              fullWidth
              label={`${t('Title')} (uz)`}
              error={!!errors.nameUz}
              helperText={
                errors.nameUz?.message ||
                (errorCreate?.data?.errors?.nameUz ? t('RequiredField') : '')
              }
              variant="outlined"
              required
              onChange={onChangeTrimStart(field.onChange)}
            />
          </>
        )}
      />
      <Controller
        name="nameRu"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <>
            {errorCreate?.data?.errors?.nameRu && (
              <p className="-mb-4 text-red-500">{t('RequiredField')}</p>
            )}
            <TextField
              {...field}
              fullWidth
              label={`${t('Title')} (рус)`}
              error={!!errors.nameRu}
              helperText={
                errors.nameRu?.message ||
                (errorCreate?.data?.errors?.nameRu ? t('RequiredField') : '')
              }
              variant="outlined"
              required
              onChange={onChangeTrimStart(field.onChange)}
            />
          </>
        )}
      />
      <Button
        disabled={isLoading || isCreateLoading || !isValid}
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default ProjectStatusForm;
